import type { QuickFillFetchResult } from '../hooks/useQuickFillData';
import {
  QUICK_FILL_LOAD_TOTAL_STEPS,
  type QuickFillLoadProgress,
} from './quickFillLoadProgress';
import type {
  QuickFillDataScope,
  QuickFillSnapshotShapeOption,
} from '../../../shared/api/grpc/familyClient';
import type { ValidationQuickFillFocus } from '../../../shared/types/validationNavigation.types';
import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';

const SAMPLE_LIMIT = 4;

export function shouldUseE2eQuickFillFallback(): boolean {
  if (
    process.env.REACT_APP_PIPEEX_E2E_BYPASS !== '1' ||
    typeof window === 'undefined'
  ) {
    return false;
  }

  return new URLSearchParams(window.location.search).get('e2e') === '1';
}

export const countFamilyTypePayloads = (families: Array<{ types?: Array<{ parameterValues?: unknown[] }> }> = []): number =>
  families.reduce((total, family) => total + (family?.types?.filter((type) => Array.isArray(type?.parameterValues) && type.parameterValues.length > 0).length ?? 0), 0);

export const countElementPayloads = (elements: Array<{ parameterValues?: unknown[] }> = []): number =>
  elements.filter((element) => Array.isArray(element?.parameterValues) && element.parameterValues.length > 0).length;

export const sampleNames = (values: string[]): string[] => values.slice(0, SAMPLE_LIMIT);

export const extractFamilyNames = (families: Array<{ name?: string }> = []): string[] =>
  sampleNames(families.map((family) => family?.name).filter(Boolean) as string[]);

export const buildSourceSummary = ({
  activePayloadSource,
  grpcHasData,
  isGrpcLoaded,
  grpcFamilies,
  grpcPipeElements,
  grpcAvailableParams,
  isFallbackLoaded,
  fallbackFamilies,
  fallbackElements,
}: {
  activePayloadSource: 'gRPC' | 'fallback';
  grpcHasData: boolean;
  isGrpcLoaded: boolean;
  grpcFamilies: Array<{ name?: string; types?: Array<{ parameterValues?: unknown[] }> }>;
  grpcPipeElements: Array<{ parameterValues?: unknown[] }>;
  grpcAvailableParams: Array<{ name?: string }>;
  isFallbackLoaded: boolean;
  fallbackFamilies: Array<{ name?: string; types?: Array<{ parameterValues?: unknown[] }> }>;
  fallbackElements: Array<{ parameterValues?: unknown[] }>;
}) => ({
  using: activePayloadSource,
  grpcLoaded: isGrpcLoaded,
  grpcFamilies: grpcFamilies.length,
  grpcFamilyTypesWithPayload: countFamilyTypePayloads(grpcFamilies),
  grpcElements: grpcPipeElements.length,
  grpcElementsWithPayload: countElementPayloads(grpcPipeElements),
  grpcAvailableParameters: grpcAvailableParams.length,
  fallbackLoaded: isFallbackLoaded,
  fallbackFamilies: fallbackFamilies.length,
  fallbackFamilyTypesWithPayload: countFamilyTypePayloads(fallbackFamilies),
  fallbackElements: fallbackElements.length,
  fallbackElementsWithPayload: countElementPayloads(fallbackElements),
  sampleGrpcFamilies: extractFamilyNames(grpcFamilies),
  sampleFallbackFamilies: extractFamilyNames(fallbackFamilies),
});

export const buildDerivedSummary = ({
  activePayloadSource,
  pipeFamilies,
  pipeElements,
  availableParameters,
}: {
  activePayloadSource: 'gRPC' | 'fallback';
  pipeFamilies: Array<{ name?: string; types?: Array<{ parameterValues?: unknown[] }> }>;
  pipeElements: Array<{ parameterValues?: unknown[] }>;
  availableParameters: Array<{ name?: string }>;
}) => ({
  source: activePayloadSource,
  pipeFamilies: pipeFamilies.length,
  pipeFamilyTypesWithPayload: countFamilyTypePayloads(pipeFamilies),
  pipeElements: pipeElements.length,
  pipeElementsWithPayload: countElementPayloads(pipeElements),
  availableParameters: availableParameters.length,
  samplePipeFamilies: extractFamilyNames(pipeFamilies),
});

export const buildRequestedColumns = (properties: Array<{ label?: string; checked?: boolean; revitParameter?: string }>): string[] =>
  properties
    .filter((property) => property?.checked)
    .map((property) => `${property.label}:${property.revitParameter || '<none>'}`);

export const buildFetchMetrics = (
  result: QuickFillFetchResult | null,
  requestedParameterCount: number,
  requestedColumnCount: number
) => {
  if (!result) {
    return [
      { label: 'Requested parameters', value: String(requestedParameterCount) },
      { label: 'Checked columns', value: String(requestedColumnCount) },
    ];
  }

  return [
    { label: 'Source', value: result.source },
    { label: 'Families', value: String(result.familiesCount) },
    { label: 'Elements', value: String(result.elementsCount) },
    { label: 'Parameters', value: String(result.availableParametersCount) },
  ];
};

export const CacheProbeFetchResult: QuickFillFetchResult = {
  success: true,
  source: 'cache',
  familiesCount: 0,
  elementsCount: 0,
  availableParametersCount: 0,
};

export const buildCacheReuseProgress = ({
  detail,
  parameterCount,
  requestedColumnCount,
  result = CacheProbeFetchResult,
  phase = 'ready',
  currentStep = QUICK_FILL_LOAD_TOTAL_STEPS,
}: {
  detail: string;
  parameterCount: number;
  requestedColumnCount: number;
  result?: QuickFillFetchResult;
  phase?: QuickFillLoadProgress['phase'];
  currentStep?: number;
}): QuickFillLoadProgress => ({
  phase,
  message: 'Quick Fill snapshot reused from cache',
  detail,
  currentStep,
  totalSteps: QUICK_FILL_LOAD_TOTAL_STEPS,
  metrics: buildFetchMetrics(result, parameterCount, requestedColumnCount),
});

export const normalizeSignatureValues = (values: string[] | undefined): string[] =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => value.trim())
        .filter(Boolean)
    )
  ).sort((left, right) => left.localeCompare(right));

export const buildQuickFillDataScopeSignature = (dataScope?: QuickFillDataScope): string => {
  if (!dataScope) {
    return 'all';
  }

  return JSON.stringify({
    familyIds: normalizeSignatureValues(dataScope.familyIds),
    familyNames: normalizeSignatureValues(dataScope.familyNames),
    familyTypeKeys: normalizeSignatureValues(dataScope.familyTypeKeys),
    elementIds: normalizeSignatureValues(dataScope.elementIds),
    categoryNames: normalizeSignatureValues(dataScope.categoryNames),
  });
};

export const buildQuickFillRefreshKey = (
  cacheScopeKey: string | null | undefined,
  snapshotShape: QuickFillSnapshotShapeOption,
  parameterNames: string[],
  dataScope?: QuickFillDataScope
): string =>
  `${cacheScopeKey?.trim() || 'unknown-document'}:${snapshotShape}:${buildQuickFillDataScopeSignature(dataScope)}:${parameterNames.slice().sort((left, right) => left.localeCompare(right)).join('|')}`;

export const buildValidationScopeSignature = (
  validationScope?: ValidationQuickFillFocus | null
): string => {
  if (!validationScope?.requestId) {
    return 'all';
  }

  return JSON.stringify({
    requestId: validationScope.requestId,
    preferredRowMode: validationScope.preferredRowMode,
    labels: normalizeSignatureValues(validationScope.labels),
    rowCandidates: normalizeSignatureValues(validationScope.rowCandidates),
    elementIds: normalizeSignatureValues(validationScope.elementIds),
    familyTypeKeys: normalizeSignatureValues(validationScope.familyTypeKeys),
  });
};

export const buildQuickFillDatasetSourceKey = (
  refreshKey: string,
  validationScope?: ValidationQuickFillFocus | null
): string => {
  const normalizedRefreshKey = refreshKey.trim();
  if (!normalizedRefreshKey) {
    return '';
  }

  return `${normalizedRefreshKey}:scope:${buildValidationScopeSignature(validationScope)}`;
};

export const uniqueScopeValues = (values: Array<string | undefined>): string[] =>
  Array.from(new Set(values.map((value) => value?.trim()).filter(Boolean) as string[]))
    .sort((left, right) => left.localeCompare(right));

export const hasQuickFillDataScope = (scope: QuickFillDataScope): boolean =>
  (scope.familyIds?.length ?? 0) > 0 ||
  (scope.familyNames?.length ?? 0) > 0 ||
  (scope.familyTypeKeys?.length ?? 0) > 0 ||
  (scope.elementIds?.length ?? 0) > 0 ||
  (scope.categoryNames?.length ?? 0) > 0;

export const buildQuickFillDataScope = (
  workflowFamilies: Array<{
    id?: string;
    name?: string;
  }>,
  workflowCategories: string[],
  validationScope?: ValidationQuickFillFocus | null
): QuickFillDataScope | undefined => {
  const validationElementIds = uniqueScopeValues(validationScope?.elementIds ?? []);
  const validationFamilyTypeKeys = uniqueScopeValues([
    ...(validationScope?.familyTypeKeys ?? []),
    ...(validationElementIds.length === 0 ? validationScope?.rowCandidates ?? [] : []),
  ]);
  const validationDataScope: QuickFillDataScope = {
    elementIds: validationElementIds,
    familyTypeKeys: validationFamilyTypeKeys,
  };

  const workflowDataScope: QuickFillDataScope = {
    familyIds: uniqueScopeValues(workflowFamilies.map((family) => family.id)),
    familyNames: uniqueScopeValues(workflowFamilies.map((family) => family.name)),
  };
  const categoryNames = uniqueScopeValues(workflowCategories);

  if (hasQuickFillDataScope(validationDataScope)) {
    return {
      ...validationDataScope,
      ...(categoryNames.length > 0 ? { categoryNames } : {}),
    };
  }

  if (hasQuickFillDataScope(workflowDataScope)) {
    return {
      ...workflowDataScope,
      ...(categoryNames.length > 0 ? { categoryNames } : {}),
    };
  }

  const categoryDataScope: QuickFillDataScope = {
    categoryNames,
  };

  return hasQuickFillDataScope(categoryDataScope) ? categoryDataScope : undefined;
};

export const buildHostProgressSignature = (progress: RevitOperationProgressSnapshot): string =>
  [
    progress.phase,
    progress.state ?? '',
    progress.message,
    progress.detail ?? '',
    progress.currentItem ?? '',
    progress.processed ?? '',
    progress.total ?? '',
    progress.percent ?? '',
    progress.cacheStatus ?? '',
    progress.resultReady ? 'ready' : '',
    progress.failed ? 'failed' : '',
    progress.error ?? '',
  ].join('|');

export const createHostProgressGate = () => {
  let lastSignature = '';

  return (progress: RevitOperationProgressSnapshot): boolean => {
    const nextSignature = buildHostProgressSignature(progress);
    if (nextSignature === lastSignature) {
      return false;
    }

    lastSignature = nextSignature;
    return true;
  };
};

export const mapHostQuickFillLoadProgress = (
  progress: RevitOperationProgressSnapshot,
  requestedParameterCount: number,
  requestedColumnCount: number
): QuickFillLoadProgress => {
  const normalizedPhase = progress.phase.toLowerCase();
  const isComplete = progress.resultReady || normalizedPhase.includes('completed');
  const isFailed = progress.failed || normalizedPhase.includes('failed');
  const phase =
    isFailed
      ? 'error'
      : isComplete
        ? 'ready'
        : normalizedPhase.includes('collecting') || normalizedPhase.includes('cache')
          ? 'processing'
          : normalizedPhase.includes('count')
            ? 'requesting'
            : 'requesting';

  return {
    operationId: progress.operationId,
    phase,
    message: progress.message || 'Loading Quick Fill data from Revit',
    detail: progress.detail,
    currentItem: progress.currentItem,
    state: progress.state,
    waitReason: progress.waitReason,
    waitElapsedMs: progress.waitElapsedMs,
    activeElapsedMs: progress.activeElapsedMs,
    currentStep: phase === 'ready' ? QUICK_FILL_LOAD_TOTAL_STEPS : phase === 'processing' ? 3 : 2,
    totalSteps: QUICK_FILL_LOAD_TOTAL_STEPS,
    processed: progress.processed,
    total: progress.total,
    percent: progress.percent,
    elapsedMs: progress.elapsedMs,
    etaMs: progress.etaMs,
    cacheStatus: progress.cacheStatus,
    resultReady: progress.resultReady,
    metrics: [
      { label: 'Requested parameters', value: String(requestedParameterCount) },
      { label: 'Checked columns', value: String(requestedColumnCount) },
      ...(progress.cacheStatus ? [{ label: 'Cache', value: progress.cacheStatus }] : []),
      ...(typeof progress.issuesFound === 'number' && progress.issuesFound > 0
        ? [{ label: 'Issues', value: String(progress.issuesFound) }]
        : []),
    ],
  };
};

export const mapFallbackElementToQuickFillPipeElement = (element: {
  id?: string;
  name?: string;
  familyName?: string;
  typeName?: string;
  typeId?: string;
  category?: string;
  parameterValues?: Array<{
    parameterId?: string;
    id?: string;
    parameterName?: string;
    name?: string;
    value?: unknown;
    isReadOnly?: boolean;
    isInstance?: boolean;
  }>;
}) => ({
  elementId: element.id,
  elementName: element.name,
  familyName: element.familyName,
  typeName: element.typeName,
  typeId: element.typeId,
  category: element.category,
  parameterValues: Array.isArray(element.parameterValues)
    ? element.parameterValues
        .filter((parameterValue) => parameterValue && (parameterValue.parameterName || parameterValue.name))
        .map((parameterValue) => ({
          parameterId: parameterValue.parameterId || parameterValue.id,
          parameterName: parameterValue.parameterName || parameterValue.name || '',
          value:
            parameterValue.value === null || parameterValue.value === undefined
              ? ''
              : String(parameterValue.value),
          isReadOnly: parameterValue.isReadOnly === true,
          isInstance: parameterValue.isInstance,
        }))
    : [],
});
